/**
 * GDELT Data Models
 * Based on the GDELT API Documentation
 */

/**
 * Country Sentiment Data Model
 */
export class CountrySentiment {
  constructor(data = {}) {
    this.Country = data.Country || '';
    this.Tone = data.Tone || 0;
    this.Polarity = data.Polarity || 0;
    this.ActivityDensity = data.ActivityDensity || 0;
    this.WordCount = data.WordCount || 0;
    this.SentimentLabel = data.SentimentLabel || 'Neutral';
    this.timestamp = data.timestamp || new Date().toISOString();
    this.source_url = data.source_url || '';
  }

  /**
   * Get sentiment color based on tone
   * @returns {string} Color hex code
   */
  getSentimentColor() {
    if (this.Tone > 5) return '#10b981'; // Green - Very Positive
    if (this.Tone > 2) return '#84cc16'; // Light Green - Positive
    if (this.Tone >= -2) return '#f59e0b'; // Amber - Neutral
    if (this.Tone >= -5) return '#f97316'; // Orange - Negative
    return '#ef4444'; // Red - Very Negative
  }

  /**
   * Get sentiment label
   * @returns {string} Sentiment label
   */
  getSentimentLabel() {
    if (this.Tone > 2) return 'Positive';
    if (this.Tone < -2) return 'Negative';
    return 'Neutral';
  }

  /**
   * Get tone percentage (normalized to 0-100)
   * @returns {number} Tone as percentage
   */
  getTonePercentage() {
    // Convert tone from -100 to +100 range to 0-100 percentage
    return ((this.Tone + 100) / 200) * 100;
  }

  /**
   * Convert to JSON
   * @returns {Object} JSON representation
   */
  toJSON() {
    return {
      Country: this.Country,
      Tone: this.Tone,
      Polarity: this.Polarity,
      ActivityDensity: this.ActivityDensity,
      WordCount: this.WordCount,
      SentimentLabel: this.SentimentLabel,
      timestamp: this.timestamp,
      source_url: this.source_url
    };
  }
}

/**
 * Country Trend Data Model
 */
export class CountryTrend {
  constructor(data = {}) {
    this.country = data.country || '';
    this.currentSentiment = data.currentSentiment ? new CountrySentiment(data.currentSentiment) : null;
    this.history = (data.history || []).map(item => new CountrySentiment(item));
    this.totalRecords = data.totalRecords || 0;
    this.trend = data.trend || 'stable'; // 'improving', 'declining', 'stable'
    this.magnitude = data.magnitude || 0;
    this.recentAverage = data.recentAverage || 0;
    this.earlierAverage = data.earlierAverage || 0;
    this.daysAnalyzed = data.daysAnalyzed || 30;
  }

  /**
   * Get trend icon
   * @returns {string} Trend icon name
   */
  getTrendIcon() {
    if (this.trend === 'improving') return 'TrendingUp';
    if (this.trend === 'declining') return 'TrendingDown';
    return 'Minus';
  }

  /**
   * Get trend color
   * @returns {string} Color hex code
   */
  getTrendColor() {
    if (this.trend === 'improving') return '#10b981';
    if (this.trend === 'declining') return '#ef4444';
    return '#6b7280';
  }

  toJSON() {
    return {
      country: this.country,
      currentSentiment: this.currentSentiment?.toJSON(),
      history: this.history.map(item => item.toJSON()),
      totalRecords: this.totalRecords,
      trend: this.trend,
      magnitude: this.magnitude,
      recentAverage: this.recentAverage,
      earlierAverage: this.earlierAverage,
      daysAnalyzed: this.daysAnalyzed
    };
  }
}

/**
 * Country Comparison Data Model
 */
export class CountryComparison {
  constructor(data = {}) {
    this.countriesCompared = data.countriesCompared || [];
    this.comparisonPeriodDays = data.comparisonPeriodDays || 30;
    this.comparisonResults = {};
    
    if (data.comparisonResults) {
      Object.keys(data.comparisonResults).forEach(country => {
        this.comparisonResults[country] = new CountrySentiment(data.comparisonResults[country]);
      });
    }
    
    this.mostPositiveCountry = data.mostPositiveCountry || '';
    this.mostNegativeCountry = data.mostNegativeCountry || '';
    this.averageTone = data.averageTone || 0;
  }

  toJSON() {
    const results = {};
    Object.keys(this.comparisonResults).forEach(country => {
      results[country] = this.comparisonResults[country].toJSON();
    });
    
    return {
      countriesCompared: this.countriesCompared,
      comparisonPeriodDays: this.comparisonPeriodDays,
      comparisonResults: results,
      mostPositiveCountry: this.mostPositiveCountry,
      mostNegativeCountry: this.mostNegativeCountry,
      averageTone: this.averageTone
    };
  }
}

/**
 * Sentiment Summary Data Model
 */
export class SentimentSummary {
  constructor(data = {}) {
    this.totalCountries = data.totalCountries || 0;
    this.averageTone = data.averageTone || 0;
    this.averagePolarity = data.averagePolarity || 0;
    this.positiveCount = data.positiveCount || 0;
    this.negativeCount = data.negativeCount || 0;
    this.neutralCount = data.neutralCount || 0;
    this.mostPositiveCountry = data.mostPositiveCountry || '';
    this.mostPositiveTone = data.mostPositiveTone || 0;
    this.mostNegativeCountry = data.mostNegativeCountry || '';
    this.mostNegativeTone = data.mostNegativeTone || 0;
  }

  toJSON() {
    return {
      totalCountries: this.totalCountries,
      averageTone: this.averageTone,
      averagePolarity: this.averagePolarity,
      positiveCount: this.positiveCount,
      negativeCount: this.negativeCount,
      neutralCount: this.neutralCount,
      mostPositiveCountry: this.mostPositiveCountry,
      mostPositiveTone: this.mostPositiveTone,
      mostNegativeCountry: this.mostNegativeCountry,
      mostNegativeTone: this.mostNegativeTone
    };
  }
}

/**
 * News Article Data Model
 */
export class NewsArticle {
  constructor(data = {}) {
    this.articleId = data.articleId || '';
    this.url = data.url || '';
    this.publishDate = data.publishDate || '';
    this.sourceName = data.sourceName || '';
    this.title = data.title || '';
    this.countries = data.countries || [];
    this.tone = data.tone || 0;
    this.positiveScore = data.positiveScore || 0;
    this.negativeScore = data.negativeScore || 0;
    this.polarity = data.polarity || 0;
    this.activityDensity = data.activityDensity || 0;
    this.wordCount = data.wordCount || 0;
    this.themes = data.themes || [];
    this.sentimentCategory = data.sentimentCategory || 'neutral';
    this.timestamp = data.timestamp || new Date().toISOString();
  }

  getSentimentColor() {
    if (this.sentimentCategory === 'positive') return '#10b981';
    if (this.sentimentCategory === 'negative') return '#ef4444';
    return '#6b7280';
  }

  toJSON() {
    return {
      articleId: this.articleId,
      url: this.url,
      publishDate: this.publishDate,
      sourceName: this.sourceName,
      title: this.title,
      countries: this.countries,
      tone: this.tone,
      positiveScore: this.positiveScore,
      negativeScore: this.negativeScore,
      polarity: this.polarity,
      activityDensity: this.activityDensity,
      wordCount: this.wordCount,
      themes: this.themes,
      sentimentCategory: this.sentimentCategory,
      timestamp: this.timestamp
    };
  }
}

/**
 * Categorized News Data Model
 */
export class CategorizedNews {
  constructor(data = {}) {
    this.country = data.country || '';
    this.sentiment = data.sentiment ? new CountrySentiment(data.sentiment) : null;
    this.positiveNewsIds = data.positiveNewsIds || [];
    this.negativeNewsIds = data.negativeNewsIds || [];
    this.neutralNewsIds = data.neutralNewsIds || [];
    this.totalArticles = data.totalArticles || 0;
    this.lastUpdated = data.lastUpdated || new Date().toISOString();
  }

  toJSON() {
    return {
      country: this.country,
      sentiment: this.sentiment?.toJSON(),
      positiveNewsIds: this.positiveNewsIds,
      negativeNewsIds: this.negativeNewsIds,
      neutralNewsIds: this.neutralNewsIds,
      totalArticles: this.totalArticles,
      lastUpdated: this.lastUpdated
    };
  }
}

/**
 * Country coordinates mapping (for map visualization)
 */
export const COUNTRY_COORDINATES = {
  'Argentina': { lat: -38.4161, lng: -63.6167 },
  'Brazil': { lat: -14.2350, lng: -51.9253 },
  'Canada': { lat: 56.1304, lng: -106.3468 },
  'China': { lat: 35.8617, lng: 104.1954 },
  'France': { lat: 46.2276, lng: 2.2137 },
  'Germany': { lat: 51.1657, lng: 10.4515 },
  'India': { lat: 20.5937, lng: 78.9629 },
  'Indonesia': { lat: -0.7893, lng: 113.9213 },
  'Italy': { lat: 41.8719, lng: 12.5674 },
  'Japan': { lat: 36.2048, lng: 138.2529 },
  'Mexico': { lat: 23.6345, lng: -102.5528 },
  'Russia': { lat: 61.5240, lng: 105.3188 },
  'South Africa': { lat: -30.5595, lng: 22.9375 },
  'United Kingdom': { lat: 55.3781, lng: -3.4360 },
  'United States': { lat: 37.0902, lng: -95.7129 }
};

/**
 * Get coordinates for a country
 * @param {string} country - Country name
 * @returns {Object} Coordinates {lat, lng}
 */
export const getCountryCoordinates = (country) => {
  return COUNTRY_COORDINATES[country] || { lat: 0, lng: 0 };
};

// Factory functions
export const createCountrySentiment = (data) => new CountrySentiment(data);
export const createCountryTrend = (data) => new CountryTrend(data);
export const createCountryComparison = (data) => new CountryComparison(data);
export const createSentimentSummary = (data) => new SentimentSummary(data);
export const createNewsArticle = (data) => new NewsArticle(data);
export const createCategorizedNews = (data) => new CategorizedNews(data);

